// CHANGE: Created comprehensive cancellation interface with retention options
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, DollarSign, ArrowDown, Info, Check } from 'lucide-react';
import { stripeService } from '../../services/stripeService';

export default function SubscriptionCancellationModal({ 
  subscription, 
  onClose, 
  onConfirmCancel,
  onDowngrade 
}) {
  const [step, setStep] = useState('reasons'); // reasons -> alternatives -> confirmation
  const [selectedReason, setSelectedReason] = useState('');
  const [customFeedback, setCustomFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [proratedRefund, setProratedRefund] = useState(0);
  const [retentionOffer, setRetentionOffer] = useState(null);

  // CHANGE: Calculate prorated refund based on remaining days
  useEffect(() => {
    if (subscription) {
      calculateProratedRefund();
      generateRetentionOffer();
    }
  }, [subscription]);

  // CHANGE: Calculate prorated refund for current billing period
  const calculateProratedRefund = () => {
    if (!subscription?.current_period_start || !subscription?.current_period_end) {
      setProratedRefund(0);
      return;
    }

    const now = new Date();
    const periodStart = new Date(subscription?.current_period_start);
    const periodEnd = new Date(subscription?.current_period_end);
    
    const totalDays = Math.ceil((periodEnd - periodStart) / (1000 * 60 * 60 * 24));
    const daysRemaining = Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24));
    const daysUsed = totalDays - daysRemaining;
    
    const dailyRate = subscription?.price_amount / totalDays;
    const refundAmount = dailyRate * daysRemaining;
    
    setProratedRefund(Math.max(0, refundAmount));
  };

  // CHANGE: Generate retention offer based on current plan
  const generateRetentionOffer = () => {
    const currentPlan = subscription?.plan;
    let offer = null;

    if (currentPlan === 'pro') {
      offer = {
        type: 'downgrade',
        targetPlan: 'advanced',
        discount: 0,
        message: 'Não precisa cancelar! Que tal fazer downgrade para o plano Avançado?'
      };
    } else if (currentPlan === 'advanced') {
      offer = {
        type: 'downgrade',
        targetPlan: 'basic',
        discount: 0,
        message: 'Não precisa cancelar! Que tal fazer downgrade para o plano Básico?'
      };
    } else {
      offer = {
        type: 'discount',
        discount: 30,
        message: 'Oferecemos 30% de desconto nos próximos 3 meses!'
      };
    }

    setRetentionOffer(offer);
  };

  // CHANGE: Cancellation reasons with retention insights
  const cancellationReasons = [
    { id: 'too_expensive', label: 'Muito caro', retentionAction: 'offer_discount' },
    { id: 'not_using', label: 'Não estou usando o suficiente', retentionAction: 'suggest_downgrade' },
    { id: 'missing_features', label: 'Faltam recursos importantes', retentionAction: 'collect_feedback' },
    { id: 'technical_issues', label: 'Problemas técnicos', retentionAction: 'offer_support' },
    { id: 'found_alternative', label: 'Encontrei uma alternativa', retentionAction: 'offer_discount' },
    { id: 'temporary_pause', label: 'Pausa temporária', retentionAction: 'suggest_pause' },
    { id: 'business_closing', label: 'Encerrando negócio', retentionAction: 'none' },
    { id: 'other', label: 'Outro motivo', retentionAction: 'collect_feedback' }
  ];

  // CHANGE: Handle reason selection and show appropriate retention strategy
  const handleReasonSelect = (reasonId) => {
    setSelectedReason(reasonId);
    const reason = cancellationReasons?.find(r => r?.id === reasonId);
    
    if (reason?.retentionAction === 'offer_discount' && subscription?.plan === 'basic') {
      // For basic plan users, offer discount
      setRetentionOffer({
        type: 'discount',
        discount: 30,
        message: 'Oferecemos 30% de desconto nos próximos 3 meses!'
      });
    }
  };

  // CHANGE: Handle downgrade option
  const handleDowngrade = async (targetPlan) => {
    setLoading(true);
    try {
      await onDowngrade(targetPlan);
      onClose();
    } catch (error) {
      console.error('Error downgrading:', error);
      alert('Erro ao fazer downgrade. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // CHANGE: Handle final cancellation
  const handleFinalCancel = async () => {
    setLoading(true);
    try {
      await onConfirmCancel({
        reason: selectedReason,
        feedback: customFeedback
      });
      onClose();
    } catch (error) {
      console.error('Error cancelling:', error);
      alert('Erro ao cancelar assinatura. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // CHANGE: Render reasons step
  const renderReasonsStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Antes de cancelar...
        </h2>
        <p className="text-gray-600">
          Nos ajude a entender o motivo do cancelamento
        </p>
      </div>

      <div className="space-y-3">
        {cancellationReasons?.map((reason) => (
          <button
            key={reason?.id}
            onClick={() => handleReasonSelect(reason?.id)}
            className={`w-full p-4 text-left border rounded-lg transition-colors ${
              selectedReason === reason?.id
                ? 'border-blue-500 bg-blue-50' :'border-gray-300 hover:border-gray-400 bg-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedReason === reason?.id
                  ? 'border-blue-500 bg-blue-500' :'border-gray-300'
              }`}>
                {selectedReason === reason?.id && (
                  <Check className="w-3 h-3 text-white" />
                )}
              </div>
              <span className="text-gray-900">{reason?.label}</span>
            </div>
          </button>
        ))}
      </div>

      {selectedReason === 'other' && (
        <textarea
          value={customFeedback}
          onChange={(e) => setCustomFeedback(e?.target?.value)}
          placeholder="Por favor, compartilhe mais detalhes..."
          className="w-full p-4 border border-gray-300 rounded-lg resize-none"
          rows={4}
        />
      )}

      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Voltar
        </button>
        <button
          onClick={() => setStep('alternatives')}
          disabled={!selectedReason}
          className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continuar
        </button>
      </div>
    </div>
  );

  // CHANGE: Render alternatives step with retention offers
  const renderAlternativesStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Considere estas alternativas
        </h2>
        <p className="text-gray-600">
          Podemos ter uma solução melhor para você
        </p>
      </div>

      {/* CHANGE: Downgrade option */}
      {retentionOffer?.type === 'downgrade' && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <ArrowDown className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">
                Fazer Downgrade de Plano
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                {retentionOffer?.message}
              </p>
              <div className="bg-white rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">
                    Plano {stripeService?.getPlanDetails(retentionOffer?.targetPlan)?.name}
                  </span>
                  <span className="text-2xl font-bold text-blue-600">
                    {stripeService?.formatAmount(stripeService?.getPlanDetails(retentionOffer?.targetPlan)?.price)}/mês
                  </span>
                </div>
                <ul className="space-y-2">
                  {stripeService?.getPlanDetails(retentionOffer?.targetPlan)?.features?.slice(0, 3)?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => handleDowngrade(retentionOffer?.targetPlan)}
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Fazer Downgrade
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CHANGE: Discount option */}
      {retentionOffer?.type === 'discount' && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <DollarSign className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">
                Oferta Especial: {retentionOffer?.discount}% de Desconto
              </h3>
              <p className="text-sm text-gray-700 mb-4">
                {retentionOffer?.message}
              </p>
              <div className="bg-white rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Novo valor mensal</p>
                    <p className="text-2xl font-bold text-green-600">
                      {stripeService?.formatAmount(
                        subscription?.price_amount * (1 - retentionOffer?.discount / 100)
                      )}/mês
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500 line-through">
                      {stripeService?.formatAmount(subscription?.price_amount)}
                    </p>
                    <p className="text-sm font-medium text-green-600">
                      Economize {retentionOffer?.discount}%
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Aceitar Desconto
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CHANGE: Prorated refund information */}
      {proratedRefund > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-gray-900 mb-1">
                Reembolso Proporcional
              </h4>
              <p className="text-sm text-gray-700">
                Se cancelar agora, você receberá um reembolso proporcional de{' '}
                <strong className="text-yellow-700">
                  {stripeService?.formatAmount(proratedRefund)}
                </strong>{' '}
                pelos {stripeService?.getDaysRemaining(subscription)} dias restantes do período atual.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => setStep('reasons')}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Voltar
        </button>
        <button
          onClick={() => setStep('confirmation')}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Prosseguir com Cancelamento
        </button>
      </div>
    </div>
  );

  // CHANGE: Render confirmation step
  const renderConfirmationStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Confirmar Cancelamento
        </h2>
        <p className="text-gray-600">
          Esta ação não pode ser desfeita facilmente
        </p>
      </div>

      <div className="bg-red-50 border border-red-200 rounded-lg p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">
            O que acontecerá:
          </h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-red-600 mt-1">•</span>
              <span>
                Sua assinatura será cancelada ao final do período atual ({' '}
                {new Date(subscription?.current_period_end)?.toLocaleDateString('pt-BR')} )
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-600 mt-1">•</span>
              <span>
                Você continuará tendo acesso aos recursos até o fim do período pago
              </span>
            </li>
            {proratedRefund > 0 && (
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>
                  Reembolso de {stripeService?.formatAmount(proratedRefund)} será processado em 5-10 dias úteis
                </span>
              </li>
            )}
            <li className="flex items-start gap-2">
              <span className="text-red-600 mt-1">•</span>
              <span>
                Todos os dados de pacientes e agendamentos serão mantidos por 90 dias
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => setStep('alternatives')}
          disabled={loading}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Voltar
        </button>
        <button
          onClick={handleFinalCancel}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Cancelando...
            </>
          ) : (
            'Confirmar Cancelamento'
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            Cancelar Assinatura
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          {step === 'reasons' && renderReasonsStep()}
          {step === 'alternatives' && renderAlternativesStep()}
          {step === 'confirmation' && renderConfirmationStep()}
        </div>
      </div>
    </div>
  );
}